import { type FC, useRef, useTransition } from 'react'
import { useIntl } from 'react-intl'

import { type UploadMetadata } from 'firebase/storage'

import { uploadFileToStorage } from '@/lib/firebase-storage'

export interface FileUploadProps {
  value?: string
  onChange: (url: string) => void
  onError?: (error: string) => void
  accept?: string
  storagePath: string
  metadata?: UploadMetadata
  disabled?: boolean
  className?: string
  height: number
  width: number
}

export const FileUpload: FC<FileUploadProps> = ({
  value,
  onChange,
  onError,
  accept = 'image/*',
  storagePath,
  metadata,
  disabled = false,
  className,
  height,
  width,
}) => {
  const intl = useIntl()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, startTransition] = useTransition()

  const handleFileSelect = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      const errorMessage = intl.formatMessage({ id: 'fileUpload.error.invalidFileType' })
      onError?.(errorMessage)
      return
    }

    try {
      const downloadURL = await uploadFileToStorage(file, storagePath, metadata)
      onChange(downloadURL)
    } catch {
      const errorMessage = intl.formatMessage({ id: 'fileUpload.error.uploadFailed' })
      onError?.(errorMessage)
    }
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      startTransition(() => handleFileSelect(file))
    }
    // Reset the input value to allow selecting the same file again
    // eslint-disable-next-line no-param-reassign
    event.target.value = ''
  }

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click()
    }
  }

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {value ? (
        <div
          className="group relative size-full cursor-pointer"
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleClick()
            }
          }}>
          <img
            src={value}
            height={height}
            width={width}
            className="size-full rounded-md border border-gray-200 object-cover transition-colors group-hover:border-blue-300"
          />
          <div className="absolute inset-0 transition-all duration-200 group-hover:backdrop-blur-xs group-hover:backdrop-opacity-60" />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-all duration-200 group-hover:opacity-100">
            <div className="">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect width="24" height="24" fill="transparent" fillOpacity="0.01" />
                <path
                  d="M0 3C0 1.34315 1.34315 0 3 0H21C22.6569 0 24 1.34315 24 3V21C24 22.6569 22.6569 24 21 24H3C1.34315 24 0 22.6569 0 21V3Z"
                  fill="#3E63DD"
                />
                <rect width="16" height="16" transform="translate(4 4)" fill="white" fillOpacity="0.01" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.5334 6.93336C12.5334 6.63881 12.2946 6.40002 12 6.40002C11.7055 6.40002 11.4667 6.63881 11.4667 6.93336V11.4667H6.93336C6.63881 11.4667 6.40002 11.7055 6.40002 12C6.40002 12.2946 6.63881 12.5334 6.93336 12.5334H11.4667V17.0667C11.4667 17.3612 11.7055 17.6 12 17.6C12.2946 17.6 12.5334 17.3612 12.5334 17.0667V12.5334H17.0667C17.3612 12.5334 17.6 12.2946 17.6 12C17.6 11.7055 17.3612 11.4667 17.0667 11.4667H12.5334V6.93336Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="group relative flex size-full cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-gray-300 transition-colors hover:border-blue-400"
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleClick()
            }
          }}>
          {isUploading ? (
            <div className="flex items-center">
              <svg className="size-5 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : (
            <div className="flex items-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect width="24" height="24" fill="white" fillOpacity="0.01" />
                <path
                  d="M0 3C0 1.34315 1.34315 0 3 0H21C22.6569 0 24 1.34315 24 3V21C24 22.6569 22.6569 24 21 24H3C1.34315 24 0 22.6569 0 21V3Z"
                  fill={disabled ? '#aaaaaa' : '#3E63DD'}
                />
                <rect width="16" height="16" transform="translate(4 4)" fill="white" fillOpacity="0.01" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.5334 6.93336C12.5334 6.63881 12.2946 6.40002 12 6.40002C11.7055 6.40002 11.4667 6.63881 11.4667 6.93336V11.4667H6.93336C6.63881 11.4667 6.40002 11.7055 6.40002 12C6.40002 12.2946 6.63881 12.5334 6.93336 12.5334H11.4667V17.0667C11.4667 17.3612 11.7055 17.6 12 17.6C12.2946 17.6 12.5334 17.3612 12.5334 17.0667V12.5334H17.0667C17.3612 12.5334 17.6 12.2946 17.6 12C17.6 11.7055 17.3612 11.4667 17.0667 11.4667H12.5334V6.93336Z"
                  fill="white"
                />
              </svg>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
